import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export const SUBSCRIPTION_TIERS = {
  monthly: {
    priceId: 'price_1SNkJvGnnUvvB9I2ZXtOpqEj',
    productId: 'prod_TKP8KuReDeFIKB',
    name: 'Monthly',
    price: '$8.99/month',
    generations: 90,
  },
  annual: {
    priceId: 'price_1SNkKMGnnUvvB9I2tvissbpE',
    productId: 'prod_TKP8BI20s9ZFV1',
    name: 'Annual',
    price: '$75.52/year',
    generations: 90,
    savings: 'Save 30%',
  },
};

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    subscribed: boolean;
    productId: string | null;
    subscriptionEnd: string | null;
    loading: boolean;
  }>({
    subscribed: false,
    productId: null,
    subscriptionEnd: null,
    loading: true,
  });
  const [generationCount, setGenerationCount] = useState<{
    used: number;
    limit: number;
    loading: boolean;
  }>({
    used: 0,
    limit: 3,
    loading: true,
  });

  const checkSubscription = useCallback(async () => {
    if (!user) {
      setSubscriptionStatus({
        subscribed: false,
        productId: null,
        subscriptionEnd: null,
        loading: false,
      });
      setGenerationCount({
        used: 0,
        limit: 3,
        loading: false,
      });
      return;
    }

    try {
      // Check for valid session before calling edge function
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.warn('No valid session, skipping subscription check');
        setSubscriptionStatus({
          subscribed: false,
          productId: null,
          subscriptionEnd: null,
          loading: false,
        });
        setGenerationCount({
          used: 0,
          limit: 3,
          loading: false,
        });
        return;
      }

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Subscription check timeout')), 8000)
      );

      const checkPromise = supabase.functions.invoke('check-subscription');
      
      const { data, error } = await Promise.race([checkPromise, timeoutPromise]) as any;
      
      if (error) {
        console.warn('Subscription check failed, using cached/default values:', error);
        // Don't throw, just use defaults
        setSubscriptionStatus(prev => ({ ...prev, loading: false }));
        setGenerationCount(prev => ({ ...prev, loading: false }));
        return;
      }

      setSubscriptionStatus({
        subscribed: data.subscribed,
        productId: data.product_id,
        subscriptionEnd: data.subscription_end,
        loading: false,
      });

      // Get generation count from profile with timeout
      const profileTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile query timeout')), 5000)
      );

      const profilePromise = supabase
        .from('profiles')
        .select('generation_count, free_generations_used, subscription_status')
        .eq('id', user.id)
        .single();

      const { data: profile } = await Promise.race([profilePromise, profileTimeoutPromise]) as any;

      if (profile) {
        const isSubscribed = profile.subscription_status === 'active';
        setGenerationCount({
          used: isSubscribed ? profile.generation_count : profile.free_generations_used,
          limit: isSubscribed ? 90 : 3,
          loading: false,
        });
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscriptionStatus(prev => ({ ...prev, loading: false }));
      setGenerationCount(prev => ({ ...prev, loading: false }));
    }
  }, [user]);

  const createCheckoutSession = async (priceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId },
      });

      if (error) throw error;

      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) throw error;

      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      throw error;
    }
  };

  useEffect(() => {
    checkSubscription();
    
    // Refresh subscription status every 5 minutes instead of 1 minute to reduce load
    const interval = setInterval(checkSubscription, 300000);
    
    return () => clearInterval(interval);
  }, [checkSubscription]);

  return {
    ...subscriptionStatus,
    generationCount,
    checkSubscription,
    createCheckoutSession,
    openCustomerPortal,
    currentTier: subscriptionStatus.productId 
      ? Object.values(SUBSCRIPTION_TIERS).find(tier => tier.productId === subscriptionStatus.productId)
      : null,
  };
};
